import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper as SwiperType } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import { images } from '../../components/shared/assets';
import { initializeDragScroll } from '../../utils/interactiveFeatures';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import { PageToggleButton } from '../../components/shared/PageToggleButton/PageToggleButton';
import '../ReEnergize/ReEnergizePage.css';

interface WellnessCard {
  id: string;
  title: string;
  location: string;
  category: string;
  description: string;
  image?: string;
}

const wellnessCards: WellnessCard[] = [
  {
    id: '1',
    title: 'Awasi Atacama invites you to RE-ENERGIZE',
    location: 'AWASI ATACAMA',
    category: 'Wellness',
    description: 'From 2-5 October 2025, Awasi Atacama hosts RE-ENERGIZE, a curated wellness retreat in the heart of the desert, designed to reconnect body, mind and soul. This edition welcomes Teca Toscano, a leading figure in self-knowledge, who will guide you through transformative practices set against the stillness and vastness of Atacama.',
    image: images.destinationsPage.properties.atacama
  },
  {
    id: '2',
    title: '3rd RE-ENERGIZE retreat with Monja Coen',
    location: 'AWASI SANTA CATARINA',
    category: 'Wellness',
    description: 'From 15 to 18 May 2025, Awasi Santa Catarina once again hosted a wellness retreat during the RE-ENERGIZE days. For this edition, in partnership with Essential Nutrition, we had the honour of welcoming Monja Coen, one of the most respected figures in the field of self-knowledge.',
    image: images.destinationsPage.properties.patagonia
  },
  {
    id: '3',
    title: 'Dive into nature\'s finest ingredients',
    location: 'AWASI MENDOZA',
    category: 'Spa',
    description: 'Experience a full menu of exclusive treatments in the heart of Mendoza\'s wine lands. Awasi Mendoza Spa offers two couple treatment rooms, a wine bath area, jacuzzi, and sauna. Our treatments combine local products, including grape seed extracts.',
    image: images.destinationsPage.properties.iguazu
  }
];

// Wellness Treatments Section Component
const WellnessTreatments: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="wellness-treatments" data-scroll-section>
      <div className="wellness-treatments__content">
        <h2>Re-Energize Retreats</h2>
        <div className="wellness-treatments__body">
          <p>
            Our goal is to provide you with a unique space to slow down and reconnect, with activities designed to nurture every aspect of your well-being. Join us on this journey of renewal and transformation.
          </p>
          <HandDrawnButton
            text="CONTACT US"
            variant="atacama"
            color="awasi"
            animation="stroke-to-fill"
            onClick={() => navigate('/atacama/contact/')}
            className="wellness-treatments__button"
          />
        </div>
      </div>
    </section>
  );
};

// Wellness Carousel Component (similar to HorizontalScrollCarousel)
const WellnessCarousel: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);


  return (
    <section className="wellness-carousel" data-scroll-section>
      <div
        ref={scrollRef}
        className="wellness-carousel__scroll-container"
        id="wellness-carousel"
      >
        {/* Panel 1: Diptych - Image left, Text + small image right */}
        <div className="wellness-carousel__panel wellness-carousel__panel--diptych">
          <div
            className="wellness-carousel__image-left"
            style={{ backgroundImage: `url('${images.reEnergizeRectangle11}')` }}
          />
          <div className="wellness-carousel__content-right">
            <div className="wellness-carousel__text-content">
              <h3>Moments to relax, reconnect and re-energize</h3>
              <p>
                Our goal is to provide you with a unique space to slow down and reconnect, with activities designed to nurture every aspect of your well-being. Join us on this journey of renewal and transformation.
              </p>
            </div>
            <div className="wellness-carousel__small-image">
              <img src={images.reEnergizeRectangle1454} alt="Small wellness image" />
            </div>
          </div>
        </div>

        {/* Panel 2: Full-screen Yoga/Meditation room */}
        <div className="wellness-carousel__panel wellness-carousel__panel--fullscreen">
          <div
            className="wellness-carousel__image-full"
            style={{ backgroundImage: `url('${images.reEnergizeRectangle1}')` }}
          />
        </div>

        {/* Panel 3: Turquoise panel with collage */}
        <div className="wellness-carousel__panel wellness-carousel__panel--turquoise">
          <div className="wellness-carousel__turquoise-left">
            <div className="wellness-carousel__turquoise-content">
              <h3>Land of many stories</h3>
              <p>
                Embrace a holistic approach to wellness with our Re-Energize program. Dive into a transformative journey that blends nature with holistic well-being, designed to rejuvenate your body, mind, and spirit.
              </p>
            </div>
          </div>
          <div className="wellness-carousel__turquoise-right">
            <div className="wellness-carousel__image-collage">
              <img src={images.reEnergizeRectangle1491} className="wellness-carousel__collage-img1" alt={images.reEnergizeRectangle1491} />
              <img src={images.reEnergizeRectangle1492} className="wellness-carousel__collage-img2" alt={images.reEnergizeRectangle1492} />
              <img src={images.reEnergizeRectangle1493} className="wellness-carousel__collage-img3" alt={images.reEnergizeRectangle1493} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Horizontal Gallery Carousel for wellness cards
const WellnessGalleryCarousel: React.FC = () => {
  const swiperRef = useRef<SwiperType | null>(null);

  const handleArrowClick = (direction: 'left' | 'right') => {
    if (swiperRef.current) {
      if (direction === 'left') {
        swiperRef.current?.slidePrev();
      } else {
        swiperRef.current?.slideNext();
      }
    }
  };

  // Extended card data to match Figma design
  const allCards = [
    ...wellnessCards,
    {
      id: '4',
      title: 'Magna gravida augue',
      location: 'awasi atacama',
      category: 'Head chef',
      description: 'Morbi faucibus urna cras arcu id elementum. Libero leo nam sollicitudin dui faucibus vitae. Eu vitae mollis in ut eget lorem vitae phasellus faucibus. In et arcu venenatis.',
      image: images.producingNaturePage.blogCards.image1
    },
    {
      id: '5',
      title: 'Magna gravida augue',
      location: 'awasi atacama',
      category: 'Head chef',
      description: 'Morbi faucibus urna cras arcu id elementum. Libero leo nam sollicitudin dui faucibus vitae. Eu vitae mollis in ut eget lorem vitae phasellus faucibus. In et arcu venenatis.',
      image: images.producingNaturePage.blogCards.image3
    }
  ];

  return (
    <section className="wellness-gallery-carousel" data-scroll-section>
      {/* Carousel Section */}
      <div className="glacier-video-exploration__carousel-container  wellness-gallery-carousel__scroll-container">
        <Swiper
          freeMode
          navigation
          spaceBetween={20}
          slidesPerView={1}
          className='glacier-video-exploration__carousel-swiper'
          breakpoints={{
            1024: {
              slidesPerView: 3,
              spaceBetween: 30,
            },
          }}
          onSwiper={(currentSwiper: SwiperType) => swiperRef.current = currentSwiper}
        >
          {allCards.map((card) => (
            <SwiperSlide key={card.id}>
              <div className="wellness-gallery-carousel__card">
                <div className="wellness-gallery-carousel__image">
                  {card.image && (
                    <img src={card.image} alt={card.title} />
                  )}
                </div>
                <div className="wellness-gallery-carousel__content">
                  <div className="wellness-gallery-carousel__header">
                    <h3>{card.title}</h3>
                    <div className="wellness-gallery-carousel__meta">
                      <span>{card.location}</span>
                      <span>-</span>
                      <span>{card.category}</span>
                    </div>
                  </div>
                  <p>{card.description}</p>
                  <CTALink
                    text="READ MORE"
                    type="primary"
                    color="awasi"
                    className="wellness-gallery-carousel__link"
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}

        </Swiper>
        <div className="glacier-video-exploration__arrow left" onClick={() => handleArrowClick('left')}>
          <img
            src={images.carouselArrow}
            alt="Scroll to see more destinations"
            className="glacier-video-exploration__arrow-image"
          />
        </div>

        <div className="glacier-video-exploration__arrow" onClick={() => handleArrowClick('right')}>
          <img
            src={images.carouselArrow}
            alt="Scroll to see more destinations"
            className="glacier-video-exploration__arrow-image"
          />
        </div>
      </div>
    </section>
  );
};

// Seasonal Produce section with large waterfall image
const SeasonalProduce: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  return (
    <section className="seasonal-produce">
      <div className="seasonal-produce__header">
        <h2>Seasonal Produce, Local Gastronomy, Based On Our Guests Preferences.</h2>
      </div>
      <div className="seasonal-produce__image-details">
        <div
          className="seasonal-produce__image"
          style={{ backgroundImage: `url('${images.fauna1}')` }}
        >
          <div className="seasonal-produce__details-box">
            <div className="seasonal-produce__content">
              <h3>Magna dignissim sed enim faucibus amet.</h3>
              <p>
                Sapien ultrices nunc ut ullamcorper rhoncus amet lectus felis. Risus non phasellus laoreet cursus. Cursus pharetra ut donec tellus mus. Tristique et augue quis tellus sed. Viverra nisi id duis aenean nisi dictumst. Aliquet urna malesuada aliquam enim duis.
              </p>
              <CTALink
                text="VIEW MORE"
                type="primary"
                color="awasi"
                onClick={() => navigate('/atacama/dining/')}
                className="seasonal-produce__link"
              />
            </div>
            <div className="seasonal-produce__pagination">
              {[1, 2, 3, 4].map(pageNum => (
                <PageToggleButton
                  key={pageNum}
                  number={pageNum}
                  isActive={currentPage === pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  color="off-white"
                  className="seasonal-produce__page-btn"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WellnessAtacama: React.FC = () => {

  return (
    <div className="re-energize-page">
      {/* Navigation - Use UnifiedNavigation with Atacama destination */}
      <UnifiedNavigation variant="light" destination="atacama" />

      {/* Hero Section - Re-energize and Thrive */}
      <section className="re-energize-hero" data-scroll-section>
        <div
          className="re-energize-hero__image"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%), url('${images.reEnergizeRectangle1}')`
          }}
        >
          <h1 className="re-energize-hero__title">Re-energize and Thrive</h1>
          <button className="re-energize-hero__volume-btn" aria-label="Toggle volume">
            {/* Volume icon would go here */}
          </button>
        </div>
      </section>

      {/* Wellness Carousel Section */}
      <WellnessCarousel />

      {/* Wellness Treatments Section */}
      <WellnessTreatments />

      {/* Horizontal Gallery Carousel */}
      <WellnessGalleryCarousel />

      {/* Seasonal Produce Section */}
      <SeasonalProduce />

      {/* Instagram Section */}
      <InstagramSection
        posts={[
          { src: images.film13, alt: "Awasi wellness" },
          { src: images.film14, alt: "Awasi nature" },
          { src: images.film15, alt: "Awasi experience" },
          { src: images.instagram4, alt: "Awasi luxury" }
        ]}
      />

      {/* Footer - Atacama variant */}
      <Footer variant="atacama" />
    </div>
  );
};

export default WellnessAtacama;